/* Experiment event/state reducer. No network transport or Optimizely SDK loading. */
(function (global) {
  'use strict';
  const EVENTS = new Set(['exposure', 'upload_started', 'upload_succeeded', 'upload_failed',
    'requested', 'revealed', 'durable_ready', 'selection', 'toggle', 'default', 'final_state',
    'failure', 'fallback', 'fallback_detail', 'fallback_progress', 'job_progress', 'cart_success', 'cart_error', 'cart_blocked', 'checkout_started',
    'order_completed', 'page_exit', 'retry', 'stage_started', 'stage_completed', 'reveal_latency', 'durable_latency',
    'choice_changed', 'final_model_choice', 'operation_started', 'operation_outcome', 'unrecovered_failure', 'cart_design']);
  const FIELDS = new Set(['designId', 'attemptId', 'jobId', 'stage', 'reason', 'recovered',
    'traceId', 'requestId', 'workerAttempt', 'phase',
    'measurementBuild', 'measurementStep', 'measurementReason',
    'rejectionGuard', 'fileBytes', 'sourceExtension', 'mimeState',
    'uploadTransport', 'uploadStatus', 'uploadBytesSent', 'uploadBytesTotal',
    'timeoutKind', 'uploadLastProgressAgeMs',
    'durationMs', 'httpStatus', 'operation', 'failureKind', 'errorName', 'timeoutMs',
    'transportPhase','attemptNumber','storageCode','candidateModel','candidateRank','previewSource','abortReason',
    'candidateCount', 'itemCount', 'designCount', 'modelId', 'position',
    'recommended', 'on', 'defaultOn', 'source', 'rankingStatus', 'finalReason', 'classification',
    'toggleCount', 'reversalCount', 'everOn', 'everOff', 'processingPerformed', 'status', 'finalRevision',
    'modelProvider', 'checkpointSha256', 'choiceSource', 'finalRank', 'operationId', 'reportingVersion', 'outcome']);
  const token = value => typeof value === 'string' && /^[A-Za-z0-9_.:-]{1,100}$/.test(value) ? value : undefined;
  const finalRankFor = (on, option) => on === false ? 'removal_off' :
    on === true && ['1','2','3'].includes(option) ? option : 'unknown';
  const copyDesign = d => ({...d,
    ...(d.modelChoice ? {modelChoice:{...d.modelChoice}} : {}),
    ...(d.finalModelChoice ? {finalModelChoice:{...d.finalModelChoice}} : {})});
  function create(config, options = {}) {
    const now = options.now || Date.now;
    const uuid = options.uuid || (() => global.crypto.randomUUID());
    let storage = options.storage;
    if (!storage) { try { storage = global.localStorage; } catch (_) {} }
    const events = [], designs = new Map(), failures = new Set(), issuedAttempts = new Map(), stageRuns = new Map();
    const operations = new Map(), operationByAttempt = new Map(), currentOperations = new Map();
    let operationSequence = 0;
    let assignment = null, sequence = 0, stageSequence = 0;
    const pageId = uuid();
    const key = `nt-bgr-${config.mode === 'draft' ? 'draft' : 'local'}:${config.experimentKey}`;
    const hostname = options.hostname || global.location?.hostname;
    const local = ['localhost', '127.0.0.1', '[::1]'].includes(hostname) && config.mode !== 'live';
    const live = config.mode === 'live' && global.NtBgrProductionScope?.allowed(config) === true;
    const editor = config.mode === 'editor' && global.NtBgrProductionScope?.editorAllowed(config) === true;
    const draft = config.mode === 'draft' && config.themeId === '164644847699' &&
      String(global.Shopify?.theme?.id) === config.themeId &&
      ['unpublished', 'development'].includes(global.Shopify?.theme?.role) &&
      (['ninjatransfers.com', 'www.ninjatransfers.com', 'transferss.myshopify.com'].includes(hostname) ||
        /^[a-z0-9-]+\.shopifypreview\.com$/.test(hostname || ''));
    function record(name, fields = {}) {
      if (!assignment || !EVENTS.has(name)) return null;
      const linked = operationFor(fields.designId, fields.attemptId);
      fields = {...fields, reportingVersion:'terminal_v1', ...(linked ? {operationId:linked.operationId} : {})};
      if (name === 'failure' && fields.attemptId && fields.stage) {
        if (!issuedAttempts.get(fields.designId ?? null)?.has(fields.attemptId)) return null;
        const failureKey=JSON.stringify([fields.designId ?? null,fields.attemptId,fields.stage]);
        if (failures.has(failureKey)) return null;
        failures.add(failureKey);
      }
      const clean = {};
      for (const [k, v] of Object.entries(fields)) {
        if (!FIELDS.has(k)) continue;
        if (k === 'timeoutKind') {
          if (['upload_idle','upload_total'].includes(v)) clean[k] = v;
          continue;
        }
        if (k === 'uploadLastProgressAgeMs') {
          if (Number.isInteger(v) && v >= 0 && v <= 240000) clean[k] = v;
          continue;
        }
        if (['fileBytes','uploadBytesSent','uploadBytesTotal'].includes(k)) {
          if (Number.isSafeInteger(v) && v >= 0 && v <= 10995116277760) clean[k] = v;
          continue;
        }
        if (typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1e9)) clean[k] = v;
        else if (token(v)) clean[k] = v;
      }
      const event = Object.freeze({eventName: `nt_bgr_${name}`, eventId: `${pageId}:${++sequence}`,
        eventSequence: sequence,
        at: new Date(now()).toISOString(), experimentKey: config.experimentKey, variation: assignment.variation,
        visitorId:assignment.visitorId, sessionId:pageId,
        qa: assignment.qa, eligible: assignment.eligible, ...clean});
      events.push(event);
      if (events.length > 1000) events.shift();
      try { options.onEvent?.(event); } catch (_) { /* Diagnostics never interrupt the builder. */ }
      return event;
    }
    function operationFor(id, attemptId) {
      if (!issuedAttempts.get(id ?? null)?.has(attemptId)) return null;
      return operations.get(operationByAttempt.get(attemptId)) || null;
    }
    function linkOperation(id, attemptId, stage) {
      const owner = id ?? null;
      let op = operations.get(currentOperations.get(owner));
      // Upload/removal retries are new customer attempts; routing and automatic
      // recovery remain children of the original operation, not new errors.
      if (owner === null || !op || op.outcome !== 'pending' ||
          stage === 'upload' && op.uploadAttempt || stage === 'removal' && op.removalAttempt) {
        if (owner !== null && op?.outcome === 'pending') settleOperation(op, 'cancelled', 'unknown', 'superseded');
        op = {operationId:`op:${++operationSequence}`, designId:owner, outcome:'pending', startedAt:now()};
        operations.set(op.operationId, op);
        if (owner !== null) currentOperations.set(owner, op.operationId);
        record('operation_started', {designId:owner, operationId:op.operationId, stage});
      }
      operationByAttempt.set(attemptId, op.operationId);
      op.lastAttempt = attemptId;
      if (stage === 'upload') op.uploadAttempt = attemptId;
      if (stage === 'removal') op.removalAttempt = attemptId;
      return op;
    }
    function settleOperation(op, outcome, stage, reason, attemptId = op?.lastAttempt) {
      if (!op || op.outcome !== 'pending') return null;
      op.outcome = outcome;
      const fields = {designId:op.designId, attemptId, operationId:op.operationId, outcome, stage, reason,
        durationMs:Math.max(0, now()-op.startedAt)};
      record('operation_outcome', fields);
      return outcome === 'failed' ? record('unrecovered_failure', fields) : fields;
    }
    function terminalFailure(id, attemptId, stage, reason) {
      const op = operationFor(id, attemptId);
      if (!op || (id != null && currentOperations.get(id) !== op.operationId)) return null;
      // An automatic route response can arrive after a manual removal starts.
      // That in-flight removal, not the obsolete default decision, owns outcome.
      if (stage === 'routing' && op.removalAttempt) return null;
      if (['cancelled','upload_cancelled'].includes(reason)) return settleOperation(op,'cancelled',stage,reason,attemptId);
      return settleOperation(op, 'failed', stage, reason, attemptId);
    }
    function operationReady(id, attemptId) {
      const op = operationFor(id, attemptId);
      if (!op || currentOperations.get(id) !== op.operationId) return null;
      if (op.outcome === 'failed' && !op.lateReady) {
        // A late/manual recovery cannot retract an already queued metric. Keep
        // the evidence explicit instead of relabelling the original failure.
        op.lateReady = true;
        return record('operation_outcome', {designId:id,attemptId,outcome:'late_ready',stage:'removal'});
      }
      return settleOperation(op,'ready','removal',undefined,attemptId);
    }
    function cancelOperation(id, reason = 'cancelled') {
      return settleOperation(operations.get(currentOperations.get(id)), 'cancelled','removal',reason);
    }
    function linkCart(ids, attemptId) {
      const run = stageRuns.get(attemptId);
      if (!run || !['cart','validation'].includes(run.stage) || run.status !== 'pending' || run.designLinks) return;
      run.designLinks = [...new Set(ids)].map(id => ({id, op:operations.get(currentOperations.get(id))})).filter(row=>row.op);
      cartLinks(run,'pending');
    }
    function cartLinks(run, outcome) {
      for (const {id,op} of run.designLinks || []) record('cart_design', {designId:id,
        operationId:op.operationId,attemptId:run.attemptId,stage:run.stage,outcome});
    }
    function assign(variation) {
      if (!local && !draft && !live && !editor) return null;
      if (assignment) return {...assignment};
      if (editor) {
        if (global.NtBgrProductionScope.editorVariation(config) !== variation) return null;
        assignment = {experimentKey:config.experimentKey,visitorId:uuid(),variation,
          assignedAt:new Date(now()).toISOString(),eligible:false,qa:true,source:'optimizely_editor'};
        record('exposure');
        return {...assignment};
      }
      if (live) {
        if (config.activation === 'default') {
          if (variation !== 'ninja_ai') return null;
          // Reuse a consented pseudonymous analytics identity when available;
          // otherwise use this page's ID. Never invent an Optimizely assignment.
          let visitorId = pageId;
          try {
            if (global.NtBgrProductionScope.consent())
              visitorId = token((options.sdk || global.optimizely)?.get?.('visitor')?.visitorId) || pageId;
          } catch (_) {}
          assignment = {experimentKey:config.experimentKey,visitorId,variation,
            assignedAt:new Date(now()).toISOString(),eligible:false,qa:false,source:'storefront_default'};
          return {...assignment};
        }
        const verified = global.NtBgrProductionScope.identity(config, options.sdk);
        if (!verified || verified.variation !== variation) return null;
        assignment = {experimentKey:config.experimentKey,visitorId:verified.visitorId,variation,
          assignedAt:new Date(now()).toISOString(),eligible:true,qa:false,source:'optimizely'};
        record('exposure');
        return {...assignment};
      }
      // A draft never randomly invents an Optimizely assignment.
      if (draft && !['control', 'ninja_ai'].includes(variation)) return null;
      let saved;
      try { saved = JSON.parse(storage?.getItem(key) || 'null'); } catch (_) {}
      if (!['control', 'ninja_ai'].includes(variation)) variation = saved?.variation;
      if (!['control', 'ninja_ai'].includes(variation)) variation = (options.random || Math.random)() < 0.5 ? 'control' : 'ninja_ai';
      assignment = {experimentKey: config.experimentKey, visitorId: token(saved?.visitorId) || uuid(),
        variation, assignedAt: saved?.variation === variation && saved?.assignedAt || new Date(now()).toISOString(),
        eligible: true, qa: true, source: draft ? 'draft_preview' : 'local_preview'};
      try { storage?.setItem(key, JSON.stringify(assignment)); } catch (_) {}
      record('exposure');
      return {...assignment};
    }
    function design(id) {
      if (!token(id)) return null;
      if (!designs.has(id)) designs.set(id, {designId:id, defaultOn:null, on:null, toggleCount:0,
        reversalCount:0, everOn:false, everOff:false, processingPerformed:false, attempt:0,
        finalized:false, finalRevision:0, classification:'unknown', choiceRevision:0, modelSelectionCount:0});
      return designs.get(id);
    }
    function setDefault(id, on, source = 'router') {
      const d = design(id);
      if (!d || d.finalized || d.defaultOn !== null || d.toggleCount || typeof on !== 'boolean') return false;
      Object.assign(d, {defaultOn:on, on, source, everOn:on, everOff:!on});
      d.choiceRevision++;
      record('default', {designId:id, on, source});
      return true;
    }
    function toggle(id, on) {
      const d = design(id);
      if (!d || typeof on !== 'boolean' || d.on === on) return false;
      if (d.toggleCount) d.reversalCount++;
      d.on = on; d.toggleCount++; d.everOn ||= on; d.everOff ||= !on;
      d.finalized = false; d.classification = 'pending';
      d.choiceRevision++;
      record('toggle', {designId:id, on, toggleCount:d.toggleCount, reversalCount:d.reversalCount});
      return true;
    }
    function observeChoice(id, fields = {}, {manual=false} = {}) {
      const d = design(id);
      if (!d) return null;
      const on = typeof fields.on === 'boolean' ? fields.on : d.on;
      const candidate = token(fields.modelId);
      const modelId = on === false ? 'removal_off' : on === true && candidate && candidate !== 'removal_off' ? candidate : 'unknown';
      const knownModel = !['removal_off','unknown'].includes(modelId);
      const choice = {on,modelId,finalRank:finalRankFor(on,fields.finalRank),modelProvider:knownModel ? token(fields.modelProvider) : undefined,
        checkpointSha256:knownModel && /^[a-f0-9]{64}$/.test(fields.checkpointSha256 || '') ? fields.checkpointSha256 : undefined};
      if (manual) d.modelSelectionCount++;
      if (manual || JSON.stringify(d.modelChoice) !== JSON.stringify(choice)) {
        d.modelChoice = choice;d.choiceRevision++;
        if (typeof on === 'boolean') d.on = on;
        d.finalized = false;d.classification = 'pending';
        record('choice_changed', {designId:id,...choice});
      }
      return {...choice,choiceRevision:d.choiceRevision};
    }
    function finalize(id, finalReason = 'cart_success', submittedChoice) {
      const d = design(id);
      if (!d || d.finalized) return null;
      // A disappearing tab and incomplete telemetry do not establish a buyer's final choice.
      if (!['cart_success', 'explicit_done'].includes(finalReason)) return null;
      // A cart response must not finalize a newer choice made while its request was pending.
      if (submittedChoice && submittedChoice.choiceRevision !== d.choiceRevision) return null;
      const choice = submittedChoice || d.modelChoice || {};
      const on = submittedChoice && typeof submittedChoice.on === 'boolean' ? submittedChoice.on : d.on;
      const modelId = on === false ? 'removal_off' : on === true && token(choice.modelId) && choice.modelId !== 'removal_off' ? choice.modelId : 'unknown';
      const choiceSource = d.toggleCount || d.modelSelectionCount ? 'manual' :
        d.defaultOn !== null && d.defaultOn === on ? 'accepted_default' : 'unknown';
      const finalRank = finalRankFor(on,choice.finalRank);
      d.on = on;
      d.finalModelChoice = {modelId,choiceSource,finalRank};
      d.finalized = true;
      d.finalRevision++;
      d.classification = d.defaultOn === null || d.on === null ? 'unknown' :
        d.defaultOn && !d.on ? 'suspected_wrong_removal' :
        !d.defaultOn && d.on ? 'suspected_bad_keep' : 'default_retained';
      // Keep model metadata separate so each Optimizely event stays within its field budget.
      record('final_model_choice', {designId:id,on,modelId,choiceSource,finalRank,finalReason,finalRevision:d.finalRevision,
        ...(!['removal_off','unknown'].includes(modelId) ? {modelProvider:choice.modelProvider,checkpointSha256:choice.checkpointSha256} : {})});
      record('final_state', {designId:id, defaultOn:d.defaultOn, on:d.on, finalReason,
        classification:d.classification, finalRevision:d.finalRevision, toggleCount:d.toggleCount, reversalCount:d.reversalCount,
        everOn:d.everOn, everOff:d.everOff, processingPerformed:d.processingPerformed});
      return copyDesign(d);
    }
    function beginRemoval(id) {
      const d = design(id);
      if (!d) return null;
      d.attempt++; d.attemptId = `${id}:${d.attempt}`; d.startedAt = now();
      if (!issuedAttempts.has(id)) issuedAttempts.set(id,new Set());
      issuedAttempts.get(id).add(d.attemptId);
      d.revealed = false; d.durable = false; d.processingPerformed = true;
      linkOperation(id, d.attemptId, 'removal');
      record('requested', {designId:id, attemptId:d.attemptId});
      return d.attemptId;
    }
    function beginStage(id, stage, source) {
      if (!assignment || !['upload', 'routing', 'ranking', 'preview', 'persistence', 'cart', 'validation'].includes(stage)) return null;
      const owner = id ?? null;
      if (owner === null ? !['cart','validation'].includes(stage) : !design(owner)) return null;
      const attemptId = `stage:${stage}:${++stageSequence}`;
      const run = {designId:owner, attemptId, stage, source:token(source), startedAt:now(), status:'pending'};
      stageRuns.set(attemptId,run);
      if (!issuedAttempts.has(owner)) issuedAttempts.set(owner, new Set());
      issuedAttempts.get(owner).add(attemptId);
      linkOperation(owner, attemptId, stage);
      record('stage_started', run);
      return attemptId;
    }
    function completeStage(id, attemptId, stage) {
      const run = stageRuns.get(attemptId);
      if (!run || run.designId !== (id ?? null) || run.stage !== stage || run.status !== 'pending') return null;
      run.status = 'succeeded';
      run.durationMs = Math.max(0, now()-run.startedAt);
      if (run.stage === 'validation' && run.designId === null) settleOperation(operationFor(null,attemptId),'ready','validation',undefined,attemptId);
      return record('stage_completed', run);
    }
    function stagePending(id, attemptId, stage) {
      const run=stageRuns.get(attemptId);
      return !!run && run.designId===(id ?? null) && run.stage===stage && run.status==='pending';
    }
    function failure(id, attemptId, stage, reason) {
      if (!issuedAttempts.get(id ?? null)?.has(attemptId)) return null;
      const run = stageRuns.get(attemptId);
      if (run) {
        if (run.designId !== (id ?? null) || run.stage !== stage || run.status !== 'pending') return null;
        run.status = 'failed';run.durationMs = Math.max(0, now()-run.startedAt);
      }
      return record('failure',{designId:id,attemptId,stage,reason,recovered:false,
        source:run?.source,durationMs:run?.durationMs});
    }
    function milestone(name, id, fields = {}) {
      const d = design(id);
      if (!d?.attempt || d[name]) return null;
      if (fields.attemptId !== d.attemptId) return null;
      d[name] = true;
      const measured = {...fields, designId:id, attemptId:d.attemptId, durationMs:Math.max(0, now()-d.startedAt)};
      const event = record(name === 'durable' ? 'durable_ready' : name, measured);
      // Optimizely ratio metrics require distinct count and numeric-value event IDs.
      // Keep the completion first, with exactly one timing sample per milestone/attempt.
      if (event) record(name === 'durable' ? 'durable_latency' : 'reveal_latency', measured);
      return event;
    }
    function cartSuccess(ids, itemCount, attemptId, choices) {
      if (attemptId && !completeStage(null,attemptId,'cart')) return null;
      const run = stageRuns.get(attemptId);
      if (run) { cartLinks(run,'ready'); settleOperation(operationFor(null,attemptId),'ready','cart',undefined,attemptId); }
      const unique = [...new Set(ids)].filter(id => designs.has(id));
      record('cart_success', {attemptId, designCount:unique.length, itemCount});
      unique.forEach(id => {
        if (choices && (!Object.hasOwn(choices,id) || !choices[id])) return;
        finalize(id, 'cart_success', choices?.[id]);
      });
    }
    function snapshot() {
      const rows = [...designs.values()].map(copyDesign);
      const settled = rows.filter(d => d.finalized && d.defaultOn !== null);
      const defaultOn = settled.filter(d => d.defaultOn), defaultOff = settled.filter(d => !d.defaultOn);
      const stageRows = [...stageRuns.values()].map(run=>({...run}));
      const stages = {};
      for (const run of stageRows) {
        const key = `${run.stage}:${run.source || 'all'}`;
        const row = stages[key] ||= {attempts:0,succeeded:0,failed:0,pending:0};
        row.attempts++;row[run.status]++;
      }
      return {assignment:assignment && {...assignment}, events:[...events], designs:rows, stageAttempts:stageRows,
        operations:[...operations.values()].map(op=>({...op})),
        diagnostics:{wrongRemovals:{count:defaultOn.filter(d=>!d.on).length,denominator:defaultOn.length},
          badKeeps:{count:defaultOff.filter(d=>d.on).length,denominator:defaultOff.length},
          removalErrors:{count:[...failures].filter(key=>JSON.parse(key)[2]==='removal').length,
            denominator:rows.reduce((sum,d)=>sum+d.attempt,0)},
          stages, unknown:rows.filter(d=>!d.finalized || d.defaultOn === null).length}};
    }
    function reset() { try { storage?.removeItem(key); } catch (_) {} }
    return {assign, record, setDefault, toggle, observeChoice, finalize, beginRemoval, beginStage, completeStage, stagePending, failure,
      terminalFailure, operationReady, cancelOperation, linkCart,
      blockedAttempt:attemptId=>{const run=stageRuns.get(attemptId);if(run)cartLinks(run,'cancelled');
        return settleOperation(operationFor(null,attemptId),'cancelled','validation','cart_gate',attemptId);},
      cartFailed:attemptId=>{ const run=stageRuns.get(attemptId); if(run)cartLinks(run,'failed'); return terminalFailure(null,attemptId,'cart','cart_request_failed'); },
      reveal:(id,fields)=>milestone('revealed',id,fields), durableReady:(id,fields)=>milestone('durable',id,fields),
      cartSuccess, snapshot, reset};
  }
  global.NtBgrExperiment = {create};
})(globalThis);
